/* eslint-disable no-console */
import { Amplify } from 'aws-amplify';
import { APIConfig, ResourcesConfig } from '@aws-amplify/core';
import ENVIRONMENT_VARIABLES from 'src/constants/environmentVariables';
import { getApiEndpoint, RestAPINames } from 'src/services/api/clients/apiConfig';
import { DevSecretKeys, eGraphQLAuthMode } from 'src/types/config/environment.types';
import { retrieveParameter } from 'src/utils/aws/parameterStore';
import { retrieveSecret } from 'src/utils/aws/secretsManager';
import { getApplicationUrl, getAwsRegion, getCognitoConfig } from '../utils/environmentUtils';

export const VERITY_DEV_APPSYNC_CONFIG_SECRET_NAME = 'verity/appsync-config-dev';
export const VERITY_APPSYNC_ENDPOINT_PARAMETER_NAME = '/verity/appsync-endpoint';
export enum AWS_REGIONS {
  US_EAST_1 = 'us-east-1',
  US_WEST_2 = 'us-west-2',
}

/**
 * Configure AWS AppSync for Verity GraphQL API interactions
 *
 * This function sets up the AppSync configuration with the appropriate
 * authentication type and endpoint for the current environment.
 * It retrieves the endpoint from either Secrets Manager (dev) or Parameter Store (other environments).
 */

/**
 * Configure AWS Amplify with Cognito settings for Midway (Amazon Federate) SSO
 *
 * This configuration uses environment-specific domains that are properly configured
 * through CDK deployment-time replacements, ensuring consistent redirect URLs
 * across all environments.
 */
export const configureAmplify = (): void => {
  const cognitoConfig = getCognitoConfig();
  const awsRegion = getAwsRegion(); // Renamed to avoid the unused variable warning
  const applicationUrl = getApplicationUrl();

  // Use the CDK-configured application URL for redirect consistency
  const redirectUrl = applicationUrl;

  // Build configuration object based on whether we're using newer or older Amplify versions
  const amplifyConfig: Record<string, unknown> = {
    Auth: {
      // User Pool configuration
      Cognito: {
        userPoolId: cognitoConfig.userPoolId,
        userPoolClientId: cognitoConfig.userPoolWebClientId,
        identityPoolId: cognitoConfig.identityPoolId,
        region: awsRegion,
        loginWith: {
          oauth: {
            domain: cognitoConfig.domain,
            scopes: ['openid'],
            redirectSignIn: [redirectUrl],
            redirectSignOut: [redirectUrl],
            responseType: 'code',
          },
        },
      },
      // Legacy cookie storage configurations
      cookieStorage: {
        domain: window.location.hostname,
        path: '/',
        secure: window.location.protocol === 'https:',
        expires: 365,
      },
      // Add region at the Auth level for older Amplify versions
      region: awsRegion,
    },
    // General AWS region config
    region: awsRegion,
  };

  // Configure Amplify with the settings - using 'any' to bypass strict type checking
  Amplify.configure(amplifyConfig);
};

/**
 * Configures both AppSync and REST APIs for Amplify
 * This function prepares and applies the complete API configuration
 */
export const configureAmplifyAPIs = async (): Promise<void> => {
  try {
    // Get existing Amplify configuration
    const existingConfig: ResourcesConfig = Amplify.getConfig();

    // Prepare AppSync configuration
    const { Stage, Region, VerityUser } = ENVIRONMENT_VARIABLES.env;
    let graphQlAPI: string = '';
    let graphQlAPIKey: string | undefined = undefined;
    let defaultAuthMode = eGraphQLAuthMode.USER_POOL;

    if (Stage === 'dev' || VerityUser) {
      const apiSecrets = (await retrieveSecret(VERITY_DEV_APPSYNC_CONFIG_SECRET_NAME, AWS_REGIONS.US_EAST_1))
        ?.SecretString;
      if (!apiSecrets) {
        throw new Error('No AppSync secrets found in Secrets Manager');
      }

      const parsedSecrets = JSON.parse(apiSecrets);
      graphQlAPI = parsedSecrets[DevSecretKeys.GRAPHQL_ENDPOINT];
      graphQlAPIKey = parsedSecrets[DevSecretKeys.API_KEY];
      defaultAuthMode = eGraphQLAuthMode.API_KEY;

      if (!graphQlAPI) {
        throw new Error(`GraphQL endpoint not found in secret`);
      }
    } else {
      graphQlAPI = await retrieveParameter(VERITY_APPSYNC_ENDPOINT_PARAMETER_NAME, AWS_REGIONS.US_EAST_1);
      if (!graphQlAPI) {
        throw new Error(`GraphQL endpoint not found in SSM parameter`);
      }
    }

    // Prepare REST API configuration
    const brassApiEndpoint = getApiEndpoint(RestAPINames.BRASS_API);
    const papiEndpoint = getApiEndpoint(RestAPINames.PAPI);

    if (!brassApiEndpoint || !papiEndpoint) {
      throw new Error('REST API endpoints not found');
    }

    // Create complete API configuration
    const apiConfig: ResourcesConfig = {
      ...existingConfig,
      API: {
        ...existingConfig.API,
        GraphQL: {
          endpoint: graphQlAPI,
          region: Region,
          defaultAuthMode,
          apiKey: graphQlAPIKey,
        },
        REST: {
          [RestAPINames.BRASS_API]: {
            endpoint: brassApiEndpoint,
            region: AWS_REGIONS.US_WEST_2,
          },
          [RestAPINames.PAPI]: {
            endpoint: papiEndpoint,
            region: AWS_REGIONS.US_WEST_2,
          },
        },
      } as APIConfig,
    };

    // Apply configuration to Amplify
    Amplify.configure(apiConfig);
    console.log('Amplify APIs configured successfully');
  } catch (error) {
    console.error('Failed to configure Amplify APIs:', error);
    throw new Error('Amplify API configuration failed');
  }
};
